import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {colors} from '../../../sharedComponents/colors'
import {Text} from '../../../Components/Atoms/Typography'

import styled, {keyframes} from 'styled-components'
import {IconSend} from '../../../Assets/Icons/TinyIcons'
import {AIDAMessage} from './AIDAWidget'
import AIDAIcon from '../../../Assets/Images/AIDANewIcon.png'

interface AIDAChatWindowProps {
  onClose?: () => void
  onMinimize?: () => void
  conversation: AIDAMessage[]
  onSendMessage: (message: string) => Promise<void>
  isLoading: boolean
}

const AIDAChatWindow = ({onClose, onMinimize, conversation, onSendMessage, isLoading}: AIDAChatWindowProps) => {
  const {t} = useTranslation('aida')
  const [currentMessage, setCurrentMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isLoading) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleSendMessage = async () => {
    if (currentMessage.trim() !== '') {
      try {
        const messageToSend = currentMessage.trim()
        setCurrentMessage('')
        await onSendMessage(messageToSend)
      } catch (error) {}
    }
  }

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [conversation])

  return (
    <ChatWindow>
      <ChatHeader>
        <Text level={2}>{t('aida:heading', 'Keskustelu')}</Text>
        <div>
          <ChatHeaderButton onClick={onMinimize}>_</ChatHeaderButton>
          <ChatHeaderButton onClick={onClose}>x</ChatHeaderButton>
        </div>
      </ChatHeader>
      <ChatMessages>
        {conversation.map((message, index) => {
          const isFirstAssistant =
            message.role === 'assistant' && !conversation.slice(0, index).some(m => m.role === 'assistant')
          return (
            <ChatBubble key={index} sender={message.role}>
              {isFirstAssistant && (
                <AssistantMeta>
                  <AssistantIconContainer>
                    <AssistantIcon src={AIDAIcon} />
                  </AssistantIconContainer>
                  <AssistantName>{t('aida:nameLabel', 'AI ASSISTENTTI AIDA')}</AssistantName>
                </AssistantMeta>
              )}
              <MessageText>{message.content}</MessageText>
              {message.sources && message.sources.length > 0 && (
                <SourcesContainer>
                  <SourceList>
                    {message.sources.map((source, index) => (
                      <SourceItem key={index}>
                        <SourceIndex>[{index + 1}]</SourceIndex> <Text level={3}> {source.source}</Text>
                      </SourceItem>
                    ))}
                  </SourceList>
                </SourcesContainer>
              )}
            </ChatBubble>
          )
        })}
        {isLoading && (
          <ChatBubble sender="assistant">
            <TypingDots>
              <Dot delay="0s" />
              <Dot delay="0.2s" />
              <Dot delay="0.4s" />
            </TypingDots>
          </ChatBubble>
        )}
        <div ref={messagesEndRef} />
      </ChatMessages>
      <ChatInputContainer>
        <ChatInput
          value={currentMessage}
          onChange={e => setCurrentMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('aida:placeholder', 'Esitä kysymys...')}
        />
        <ChatSendButton onClick={handleSendMessage} disabled={!currentMessage.trim()}>
          <IconSend />
        </ChatSendButton>
      </ChatInputContainer>
      <FooterMessage>
        {t('aida:informationText', 'Keskustelet tekoälyn kanssa ja se voi tehdä virheitä. Tarkista tärkeät tiedot.')}
      </FooterMessage>
    </ChatWindow>
  )
}

export const MessageText = styled.span`
  font-size: 0.8rem; /* 14px */
  line-height: 1.5rem; /* 24px */
  letter-spacing: 0.25px;
  font-weight: normal;
  display: block;
  margin: 0;
`

const ChatWindow = styled.div`
  width: 40vw;
  height: 80vh;
  max-width: 800px;
  background: ${colors.system.white};
  border: 1px solid #ccc;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 4000;
  position: relative;
`

const ChatHeader = styled.div`
  background: ${colors.brand.cobalt};
  color: ${colors.system.white};
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ChatHeaderButton = styled.button`
  background: transparent;
  color: white;
  border: none;
  font-size: 18px;
  margin-left: 8px;
  cursor: pointer;
`

const ChatMessages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  padding: 12px;
  padding-top: 20px;
  overflow-y: auto;
  background: ${colors.system.white};
`

const ChatInputContainer = styled.div`
  padding: 8px;
  display: flex;
  border-top: 1px solid #ccc;
`

const ChatInput = styled.textarea`
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  resize: none;
  font-size: 0.75rem;
  line-height: 1.25rem;
  letter-spacing: 0.25px;
  display: block;
`

const ChatSendButton = styled.button`
  margin-left: 8px;
  padding: 8px 12px;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, color 0.2s ease;
  &:hover {
    transform: scale(1.2);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

interface ChatBubbleProps {
  sender: 'user' | 'assistant'
}

const ChatBubble = styled.div<ChatBubbleProps>`
  position: relative;
  align-self: ${({sender}) => (sender === 'user' ? 'flex-end' : 'flex-start')};
  background-color: ${({sender}) => (sender === 'user' ? colors.brand.citrone : '#e5e5ea')};
  color: ${colors.brand.black};
  padding: ${({sender}) => (sender === 'assistant' ? '20px 15px 10px 15px' : '10px 15px')};
  border-radius: 20px;
  max-width: 85%;
  line-height: 1.4;
  white-space: pre-wrap;
  margin-left: ${({sender}) => (sender === 'assistant' ? '10px' : '0')};
  overflow-wrap: break-word;
  margin-top: 5px;
`

const SourcesContainer = styled.div`
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #ccc;
  font-size: 0.75rem;
  color: ${colors.brand.cobalt};
`

const SourceList = styled.ul`
  list-style: none;
  margin: 0;
`

const SourceItem = styled.li`
  margin-bottom: 4px;
  line-height: 1.4;
  white-space: pre-wrap;
`
const SourceIndex = styled.span`
  font-weight: bold;
  margin-right: 4px;
`

const TypingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
`

const bounce = keyframes`
    0%, 80%, 100% {
        transform: translateY(0);
        opacity: 0.3;
    }
    40% {
        transform: translateY(-6px);
        opacity: 1;
    }
`

const Dot = styled.span<{delay: string}>`
  width: 6px;
  height: 6px;
  background-color: ${colors.brand.cobalt};
  border-radius: 50%;
  opacity: 0.3;
  animation: ${bounce} 1.4s infinite;
  animation-delay: ${({delay}) => delay};
`

const FooterMessage = styled.div`
  font-size: 0.7rem;
  color: #999;
  align-self: center;
  margin-top: 4px;
  padding-bottom: 4px;
  padding-left: 25px;
`

const AssistantMeta = styled.div`
  position: absolute;
  top: -20px;
  left: -20px;
  display: flex;
  align-items: center;
  gap: 6px;
`

const AssistantIconContainer = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${colors.brand.cobalt};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-shadow: 0 0 0 1px white;
`

const AssistantIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: scale-down;
  border-radius: 50%;
  background-color: ${colors.system.white};
`
const AssistantName = styled.span`
  font-size: 0.5rem;
  color: #999;
  font-weight: 500;
`
export default AIDAChatWindow
